import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Avatar,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { CakeRounded as CakeIcon } from '@mui/icons-material';
import { GET_UPCOMING_BIRTHDAYS, GET_PERSONS } from '../apollo/queries';
import {
  getDisplayName,
  getInitials,
  calculateAge,
  formatBirthday,
  getDaysUntilBirthday,
  getPersonAvatarUrl,
} from '../hooks/usePersonHelpers';

const TIMEZONE = 'America/New_York';

function BirthdaysPage() {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GET_UPCOMING_BIRTHDAYS, {
    variables: { limit: 50 },
  });
  const { data: allPersonsData } = useQuery(GET_PERSONS);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading birthdays: {error.message}</Alert>;
  }

  const persons = data?.upcomingBirthdays || [];
  const now = DateTime.now().setZone(TIMEZONE);
  const currentMonth = now.monthLong;

  // Get all persons with birthdays, sorted chronologically by month/day
  const allPersonsWithBirthdays = (allPersonsData?.persons || [])
    .filter((p) => p.birthday && !p.deceasedDate)
    .sort((a, b) => {
      const dateA = DateTime.fromISO(a.birthday, { zone: TIMEZONE });
      const dateB = DateTime.fromISO(b.birthday, { zone: TIMEZONE });
      // Sort by month first, then by day
      if (dateA.month !== dateB.month) {
        return dateA.month - dateB.month;
      }
      return dateA.day - dateB.day;
    });

  // Group by month for display
  const birthdaysByMonth = allPersonsWithBirthdays.reduce((acc, person) => {
    const date = DateTime.fromISO(person.birthday, { zone: TIMEZONE });
    const monthName = date.monthLong;
    if (!acc[monthName]) {
      acc[monthName] = [];
    }
    acc[monthName].push(person);
    return acc;
  }, {});

  const monthOrder = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Separate today's birthdays, this week, this month, and later
  const todaysBirthdays = [];
  const thisWeekBirthdays = [];
  const thisMonthBirthdays = [];
  const laterBirthdays = [];

  persons.forEach((person) => {
    const daysUntil = getDaysUntilBirthday(person.birthday);
    if (daysUntil === 0) {
      todaysBirthdays.push(person);
    } else if (daysUntil <= 7) {
      thisWeekBirthdays.push(person);
    } else if (daysUntil <= 30) {
      thisMonthBirthdays.push(person);
    } else {
      laterBirthdays.push(person);
    }
  });

  const BirthdayCard = ({ person }) => {
    const daysUntil = getDaysUntilBirthday(person.birthday);
    const age = calculateAge(person.birthday);
    const turningAge = age !== null ? age + (daysUntil > 0 ? 1 : 0) : null;
    const birthDate = DateTime.fromISO(person.birthday, { zone: TIMEZONE });

    return (
      <Card>
        <CardActionArea onClick={() => navigate(`/person/${person.id}`)}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={getPersonAvatarUrl(person, 56)}
              alt={`Photo of ${getDisplayName(person)}`}
              sx={{ width: 56, height: 56 }}
            >
              {getInitials(person)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={500}>
                {getDisplayName(person)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {birthDate.toFormat('MMMM d')}
                {turningAge !== null && ` • Turning ${turningAge}`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {daysUntil === 0 ? (
                <Chip
                  icon={<CakeIcon />}
                  label="Today! 🎉"
                  color="secondary"
                  size="small"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {daysUntil} day{daysUntil !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const Section = ({ title, persons, emptyMessage }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CakeIcon color="primary" />
        {title}
      </Typography>
      {persons.length > 0 ? (
        <Grid container spacing={2}>
          {persons.map((person) => (
            <Grid item xs={12} sm={6} md={4} key={person.id}>
              <BirthdayCard person={person} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography color="text.secondary">{emptyMessage}</Typography>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        🎂 Birthdays
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Keep track of upcoming family birthdays
      </Typography>

      {todaysBirthdays.length > 0 && (
        <Section title="Today's Birthdays 🎉" persons={todaysBirthdays} emptyMessage="" />
      )}

      <Section
        title="This Week"
        persons={thisWeekBirthdays}
        emptyMessage="No birthdays this week"
      />

      <Section
        title="Next 30 Days"
        persons={thisMonthBirthdays}
        emptyMessage="No birthdays in the next 30 days"
      />

      {/* All Birthdays by Month */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="h5" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        📅 All Birthdays
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Everyone's birthday in calendar order
      </Typography>

      {monthOrder.map((month) => {
        const monthPersons = birthdaysByMonth[month];
        if (!monthPersons || monthPersons.length === 0) return null;

        return (
          <Box key={month} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                mb: 1
              }}
            >
              {month}
            </Typography>
            <List dense>
              {monthPersons.map((person) => {
                const birthDate = DateTime.fromISO(person.birthday, { zone: TIMEZONE });
                const age = calculateAge(person.birthday);
                return (
                  <ListItem
                    key={person.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderRadius: 1,
                    }}
                    onClick={() => navigate(`/person/${person.id}`)}
                  >
                    <ListItemAvatar>
                      <Avatar src={person.profilePictureUrl} sx={{ width: 36, height: 36 }}>
                        {getInitials(person)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getDisplayName(person)}
                      secondary={age !== null ? `Age ${age}` : null}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
                      {birthDate.toFormat('MMM d')}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        );
      })}
    </Box>
  );
}

export default BirthdaysPage;

